import { useMemo } from 'react';
import { CONFIG } from '../../leadership/api/LeadershipServices';

export const useLeadershipRatings = (performanceData) => {
    const processRatings = (reviewPeriod) => {
        const ratingCounts = {
            1: 0,
            2: 0,
            3: 0,
            4: 0,
            5: 0
        };
        
        let total = 0;
        let totalScore = 0;
        const rawEvents = []; // Store raw events with ratings
        
        // Define review period date ranges based on your fiscal year
        // Mid-Year: July 1 - December 31, End-Year: January 1 - June 30
        const getReviewPeriodFromDate = (dateString) => {
            if (!dateString) return null;
            const date = new Date(dateString);
            const month = date.getMonth(); // 0-11
            // January-June (months 0-5) -> End-Year
            // July-December (months 6-11) -> Mid-Year
            if (month >= 0 && month <= 5) {
                return 'End-Year';
            } else {
                return 'Mid-Year';
            }
        };
        
        // Search through ALL collections for rating values
        const collectionsToSearch = [
            { name: 'objectives', data: performanceData?.objectives?.events || [] },
            { name: 'kpis', data: performanceData?.kpis?.events || [] },
            { name: 'kras', data: performanceData?.kras?.events || [] },
            { name: 'tasks', data: performanceData?.tasks?.events || [] },
            { name: 'goals', data: performanceData?.goals?.events || [] },
            { name: 'leadership', data: performanceData?.leadership?.events || [] },
            { name: 'behaviors', data: performanceData?.behaviors?.events || [] },
        ];
        
        let allRatingEvents = [];
        
        for (const collection of collectionsToSearch) {
            if (collection.data && collection.data.length > 0) {
                const eventsWithRatings = collection.data.filter(event => {
                    const dataValues = event.dataValues || [];
                    return dataValues.some(dv => {
                        const num = parseInt(dv.value);
                        return !isNaN(num) && num >= 1 && num <= 5;
                    });
                });
                
                if (eventsWithRatings.length > 0) {
                    console.log(`   Found ${eventsWithRatings.length} rating events in ${collection.name}`);
                    allRatingEvents.push(...eventsWithRatings.map(e => ({ ...e, _source: collection.name })));
                }
            }
        }
        
        console.log(`📊 Total rating events found: ${allRatingEvents.length}`);
        
        // Process each rating event
        allRatingEvents.forEach(event => {
            const dataValues = event.dataValues || [];
            let rating = null;
            let eventPeriod = null;
            
            // Extract rating value
            for (const dv of dataValues) {
                const num = parseInt(dv.value);
                if (!isNaN(num) && num >= 1 && num <= 5) {
                    rating = num;
                    break;
                }
            }
            
            if (!rating) return;
            
            // Method 1: Check for explicit review period in data values
            for (const dv of dataValues) {
                const val = dv.value;
                if (typeof val === 'string') {
                    const lowerVal = val.toLowerCase();
                    if (lowerVal === 'mid-year' || lowerVal === 'mid year' || lowerVal === 'midyear') {
                        eventPeriod = 'Mid-Year';
                        break;
                    } else if (lowerVal === 'end-year' || lowerVal === 'end year' || lowerVal === 'endyear') {
                        eventPeriod = 'End-Year';
                        break;
                    }
                }
            }
            
            // Method 2: Check program stage
            if (!eventPeriod) {
                const programStage = event.programStage;
                if (programStage === CONFIG?.stages?.midYear) {
                    eventPeriod = 'Mid-Year';
                } else if (programStage === CONFIG?.stages?.endYear) {
                    eventPeriod = 'End-Year';
                }
            }
            
            // Method 3: Determine by event date
            if (!eventPeriod) {
                const eventDate = event.eventDate || event.createdAt;
                eventPeriod = getReviewPeriodFromDate(eventDate);
                if (eventPeriod) {
                    console.log(`   Determined period from date (${eventDate}): ${eventPeriod}`);
                }
            }
            
            // Method 4: If still no period, use the event's source collection as hint
            if (!eventPeriod) {
                const createdAt = event.createdAt || event.eventDate;
                if (createdAt) {
                    eventPeriod = getReviewPeriodFromDate(createdAt);
                }
            }
            
            // Match with requested review period
            if (eventPeriod === reviewPeriod || (!eventPeriod && reviewPeriod === 'End-Year')) {
                // If no period detected and we're looking for End-Year, include it as fallback
                if (!eventPeriod && reviewPeriod === 'End-Year') {
                    ratingCounts[rating]++;
                    total++;
                    totalScore += rating;
                    rawEvents.push(event); // Store the raw event
                    console.log(`   ✅ Found ${reviewPeriod} rating (fallback): ${rating} from ${event._source}`);
                } else if (eventPeriod === reviewPeriod) {
                    ratingCounts[rating]++;
                    total++;
                    totalScore += rating;
                    rawEvents.push(event); // Store the raw event
                    console.log(`   ✅ Found ${reviewPeriod} rating: ${rating} from ${event._source}`);
                }
            }
        });
        
        const average = total > 0 ? (totalScore / total).toFixed(2) : 0;
        
        const chartData = [1, 2, 3, 4, 5].map(rating => ({
            rating: rating,
            count: ratingCounts[rating],
            percentage: total > 0 ? ((ratingCounts[rating] / total) * 100).toFixed(1) : 0
        }));
        
        console.log(`📊 ${reviewPeriod} Review Results:`, { 
            total, 
            average, 
            distribution: ratingCounts,
            hasData: total > 0,
            rawEventsCount: rawEvents.length
        });
        
        return {
            data: chartData,
            total,
            average,
            distribution: ratingCounts,
            rawEvents: rawEvents // This is the key addition!
        };
    };
    
    const getRatingLabel = (rating) => {
        switch(rating) {
            case 1: return 'Poor';
            case 2: return 'Below Average';
            case 3: return 'Average';
            case 4: return 'Above Average';
            case 5: return 'Excellent';
            default: return '';
        }
    };
    
    const midYearRatings = useMemo(() => processRatings('Mid-Year'), [performanceData]);
    const endYearRatings = useMemo(() => processRatings('End-Year'), [performanceData]);
    
    return {
        midYearRatings,
        endYearRatings,
        processRatings
    };
};